import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { geoguesserService } from '../../../services/api';
import MapCoordinateCanvas from '../../MapCoordinateCanvas';
import MapPinSelector from '../../MapPinSelector';
import PhotoZoomModal from '../../PhotoZoomModal';
import ProgressiveImage from '../../common/ProgressiveImage';
import BulkLevelImportWizard from './BulkLevelImportWizard';

/**
 * Self-contained level-assignment UI for a single GeoGuesser map.
 *
 * Responsibilities:
 *   - Load the map's levels and render a card per level with photo,
 *     current saved answer, target-floor dropdown, and click-to-re-pin
 *     floor preview.
 *   - Auto-save target-floor picks and pending pins to the per-map
 *     migration draft (opaque JSON blob stored server-side) so the
 *     wizard / editor can be closed and reopened without losing work.
 *   - Commit the full level (floor + coords) to the levels endpoint
 *     when the admin clicks Save on a card.
 *   - Open a full-screen zoom/pan pin picker for precision work.
 *
 * Draft persistence is opt-in via `enableDraft`. The migration wizard
 * wants it (admins walk away and come back); the map editor usually
 * wants it too but it's a prop so we stay flexible.
 *
 * Props:
 *   - `mapId`        – required
 *   - `floors`       – floor list from the sibling FloorManager. Used
 *                      to build the target-floor dropdown. Passing it
 *                      in (rather than re-loading) keeps the dropdown
 *                      perfectly in sync with the Floors section.
 *   - `floorBuffers` – optional per-floor edit buffers from the
 *                      FloorManager so dropdown labels reflect live
 *                      renames before they've been PATCHed.
 *   - `mapInfo`      – full map object; used for `map_image` (the
 *                      legacy pre-migration image the saved answers
 *                      were pinned against).
 *   - `enableDraft`  – default true.
 *   - `onError`, `onSuccess` – banner / toast hooks.
 */
const errorDetail = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'object' && data) {
    const entries = Object.entries(data);
    if (entries.length > 0) {
      return entries
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('; ');
    }
  }
  return data?.detail || fallback;
};

const LevelAssigner = ({
  mapId,
  floors,
  floorBuffers = {},
  mapInfo,
  enableDraft = true,
  onError,
  onSuccess,
}) => {
  const [levels, setLevels] = useState([]);
  const [levelBuffers, setLevelBuffers] = useState({});
  const [loading, setLoading] = useState(true);
  const [pinningLevelId, setPinningLevelId] = useState(null);
  const [zoomLevelId, setZoomLevelId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [showBulkWizard, setShowBulkWizard] = useState(false);
  const [singleAddOpen, setSingleAddOpen] = useState(false);
  const [singleName, setSingleName] = useState('');
  const [singleFloorId, setSingleFloorId] = useState('');
  const [singleDifficulty, setSingleDifficulty] = useState('medium');
  const [singleHint, setSingleHint] = useState('');
  const [singlePhotoFile, setSinglePhotoFile] = useState(null);
  const [singlePhotoPreview, setSinglePhotoPreview] = useState(null);
  const [singleAnswer, setSingleAnswer] = useState(null);
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  const [selectedLevelIds, setSelectedLevelIds] = useState([]);

  // Draft persistence state + plumbing.
  const [draftOverlay, setDraftOverlay] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const draftOverlayRef = useRef({});
  const autosaveTimerRef = useRef(null);
  const pendingSaveRef = useRef(false);
  const mapIdRef = useRef(null);
  useEffect(() => {
    mapIdRef.current = mapId;
  }, [mapId]);

  const reportError = useCallback(
    (msg) => {
      if (typeof onError === 'function') onError(msg);
    },
    [onError],
  );
  const reportSuccess = useCallback(
    (msg) => {
      if (typeof onSuccess === 'function') onSuccess(msg);
    },
    [onSuccess],
  );

  const floorById = useMemo(() => {
    const m = {};
    for (const f of floors || []) m[f.id] = f;
    return m;
  }, [floors]);

  // ----- Draft autosave --------------------------------------------------

  const saveDraftNow = useCallback(() => {
    if (!enableDraft) return Promise.resolve();
    const pk = mapIdRef.current;
    if (!pk) return Promise.resolve();
    const overlay = draftOverlayRef.current || {};
    const isEmpty = Object.keys(overlay).length === 0;
    pendingSaveRef.current = false;
    const req = isEmpty
      ? geoguesserService.deleteMigrationDraft(pk)
      : geoguesserService.saveMigrationDraft(pk, { levels: overlay });
    return req.catch((e) => {
      pendingSaveRef.current = true;
      // eslint-disable-next-line no-console
      console.warn('Level draft autosave failed', e);
    });
  }, [enableDraft]);

  const scheduleDraftSave = useCallback(() => {
    if (!enableDraft) return;
    pendingSaveRef.current = true;
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      saveDraftNow();
    }, 500);
  }, [enableDraft, saveDraftNow]);

  const updateDraftOverlay = useCallback(
    (updater) => {
      const next =
        typeof updater === 'function'
          ? updater(draftOverlayRef.current)
          : updater;
      draftOverlayRef.current = next;
      setDraftOverlay(next);
      scheduleDraftSave();
    },
    [scheduleDraftSave],
  );

  // Flush pending save on unmount / beforeunload — the debounce
  // timer would otherwise get cancelled mid-typing.
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      if (pendingSaveRef.current) saveDraftNow();
    };
  }, [saveDraftNow]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (!pendingSaveRef.current) return;
      saveDraftNow();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveDraftNow]);

  // ----- Data loading ----------------------------------------------------

  const loadLevels = useCallback(async () => {
    if (!mapId) return;
    try {
      const res = await geoguesserService.getLevels(mapId, null, true);
      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.results || [];
      setLevels(list);
      const overlay = draftOverlayRef.current || {};
      const buffers = {};
      for (const lvl of list) {
        const currentFloorId =
          lvl.floor_id ||
          lvl.floor?.id ||
          (typeof lvl.floor === 'number' ? lvl.floor : null);
        const base = {
          floor_id: currentFloorId,
          answer_x: lvl.answer_x,
          answer_y: lvl.answer_y,
          pending_answer: null,
        };
        const overrides = overlay[lvl.id];
        if (overrides && typeof overrides === 'object') {
          if ('floor_id' in overrides) base.floor_id = overrides.floor_id;
          if ('pending_answer' in overrides) {
            base.pending_answer = overrides.pending_answer;
          }
        }
        buffers[lvl.id] = base;
      }
      setLevelBuffers(buffers);
    } catch (err) {
      reportError(errorDetail(err, 'Failed to load levels.'));
    } finally {
      setLoading(false);
    }
  }, [mapId, reportError]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mapId) return;
      if (enableDraft) {
        try {
          const res = await geoguesserService.getMigrationDraft(mapId);
          if (cancelled) return;
          const levelsDraft = res?.data?.data?.levels;
          if (levelsDraft && typeof levelsDraft === 'object') {
            draftOverlayRef.current = levelsDraft;
            setDraftOverlay(levelsDraft);
          }
        } catch (_err) {
          // 204 / network / no draft → silently treat as empty.
        }
      }
      await loadLevels();
      if (!cancelled) setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [mapId, enableDraft, loadLevels]);

  // ----- Level handlers --------------------------------------------------

  const handleLevelBufferChange = (id, patch) => {
    setLevelBuffers((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    const overlayPatch = {};
    if ('floor_id' in patch) overlayPatch.floor_id = patch.floor_id;
    if ('pending_answer' in patch) {
      overlayPatch.pending_answer = patch.pending_answer;
    }
    if (Object.keys(overlayPatch).length === 0) return;
    updateDraftOverlay((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...overlayPatch },
    }));
  };

  const handlePinSelect = (levelId, x, y) => {
    handleLevelBufferChange(levelId, { pending_answer: { x, y } });
  };

  // Does a given level have an unsaved target-floor change or pending pin?
  const levelHasPending = (lvl) => {
    const buf = levelBuffers[lvl.id];
    if (!buf) return false;
    const currentFloorId =
      lvl.floor_id ||
      lvl.floor?.id ||
      (typeof lvl.floor === 'number' ? lvl.floor : null);
    if (buf.pending_answer) return true;
    if (buf.floor_id != null && buf.floor_id !== currentFloorId) return true;
    return false;
  };

  const pendingLevels = useMemo(
    () => levels.filter(levelHasPending),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [levels, levelBuffers],
  );

  // Bulk save: commit every level with pending changes in parallel,
  // then clear those rows from the draft overlay and reload server
  // truth. The LevelAssigner purposely does NOT show a Save button
  // on each row — one action at the bottom commits everything.
  const handleSaveAllPending = async () => {
    if (savingAll) return;
    const targets = pendingLevels;
    if (targets.length === 0) return;
    setSavingAll(true);
    const savedIds = [];
    const failures = [];
    await Promise.all(
      targets.map(async (level) => {
        const buf = levelBuffers[level.id];
        if (!buf) return;
        const payload = new FormData();
        if (buf.floor_id) payload.append('floor', buf.floor_id);
        const x = buf.pending_answer?.x ?? buf.answer_x;
        const y = buf.pending_answer?.y ?? buf.answer_y;
        if (x != null) payload.append('answer_x', String(x));
        if (y != null) payload.append('answer_y', String(y));
        try {
          await geoguesserService.updateLevel(level.id, payload);
          savedIds.push(level.id);
        } catch (err) {
          failures.push({
            level,
            message: errorDetail(err, 'save failed'),
          });
        }
      }),
    );
    if (savedIds.length > 0) {
      updateDraftOverlay((prev) => {
        let next = prev;
        for (const id of savedIds) {
          if (id in next) {
            if (next === prev) next = { ...prev };
            delete next[id];
          }
        }
        return next;
      });
    }
    await loadLevels();
    setSavingAll(false);
    if (failures.length === 0) {
      reportSuccess(
        `Saved ${savedIds.length} level${savedIds.length === 1 ? '' : 's'}.`,
      );
    } else {
      const summary = failures
        .map((f) => `${f.level.name || `Level ${f.level.id}`}: ${f.message}`)
        .join('; ');
      reportError(
        savedIds.length > 0
          ? `Saved ${savedIds.length}, ${failures.length} failed — ${summary}`
          : `Failed to save: ${summary}`,
      );
    }
  };

  const handleResetDraft = async () => {
    if (
      !window.confirm(
        'Discard all in-progress target-floor and re-pin changes on this map? ' +
          'Floors and levels you have already saved are kept.',
      )
    )
      return;
    try {
      await geoguesserService.deleteMigrationDraft(mapId);
    } catch (_err) {
      // Swallow — we still want to clear client-side state below.
    }
    updateDraftOverlay({});
    await loadLevels();
    reportSuccess('Draft changes discarded.');
  };

  // Progressive bundle / legacy URL for a floor image (used throughout).
  const pickFloorImage = (floor) =>
    floor?.floor_image_urls || floor?.floor_image || null;

  const resolveFloorCanvasImage = (floor) => {
    if (!floor) return null;
    const b = floorBuffers[floor.id];
    if (b?.file && b.filePreview) return b.filePreview;
    return pickFloorImage(floor);
  };

  const resetSingleAddForm = () => {
    setSinglePhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setSinglePhotoFile(null);
    setSingleName('');
    setSingleFloorId('');
    setSingleDifficulty('medium');
    setSingleHint('');
    setSingleAnswer(null);
  };

  const toggleSingleAdd = () => {
    if (singleAddOpen) {
      resetSingleAddForm();
      setSingleAddOpen(false);
    } else {
      if (floors?.length) setSingleFloorId(floors[0].id);
      setSingleAddOpen(true);
    }
  };

  const handleSinglePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSinglePhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setSinglePhotoFile(file);
    e.target.value = '';
  };

  const handleCreateSingleLevel = async (e) => {
    e.preventDefault();
    if (!(singleName || '').trim()) {
      reportError('Enter a name for the level.');
      return;
    }
    if (!singleFloorId) {
      reportError('Select a floor.');
      return;
    }
    if (!singlePhotoFile) {
      reportError('Choose a photo for the level.');
      return;
    }
    if (
      singleAnswer == null ||
      typeof singleAnswer.x !== 'number' ||
      typeof singleAnswer.y !== 'number'
    ) {
      reportError('Place the answer on the floor map.');
      return;
    }
    setSingleSubmitting(true);
    const createdLabel = singleName.trim();
    try {
      const fd = new FormData();
      fd.append('geogusser_map', String(mapId));
      fd.append('floor', String(singleFloorId));
      fd.append('name', createdLabel);
      fd.append('difficulty', singleDifficulty);
      fd.append('hint', (singleHint || '').trim());
      fd.append('answer_x', String(singleAnswer.x));
      fd.append('answer_y', String(singleAnswer.y));
      fd.append('photo', singlePhotoFile);
      await geoguesserService.createLevel(fd);
      resetSingleAddForm();
      setSingleAddOpen(false);
      await loadLevels();
      reportSuccess(`Created level "${createdLabel}".`);
    } catch (err) {
      reportError(errorDetail(err, 'Could not create level.'));
    } finally {
      setSingleSubmitting(false);
    }
  };

  const handleDeleteLevel = async (lvl) => {
    const label = lvl.name || `Level ${lvl.id}`;
    if (
      !window.confirm(
        `Delete level "${label}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await geoguesserService.deleteLevel(lvl.id);
      updateDraftOverlay((prev) => {
        if (!(lvl.id in prev)) return prev;
        const next = { ...prev };
        delete next[lvl.id];
        return next;
      });
      setSelectedLevelIds((ids) => ids.filter((x) => x !== lvl.id));
      await loadLevels();
      reportSuccess(`Deleted "${label}".`);
    } catch (err) {
      reportError(errorDetail(err, 'Delete failed.'));
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedLevelIds.length === 0) return;
    const n = selectedLevelIds.length;
    if (
      !window.confirm(
        `Delete ${n} level${n === 1 ? '' : 's'}? This cannot be undone.`,
      )
    ) {
      return;
    }
    const failures = [];
    const succeeded = [];
    for (const id of selectedLevelIds) {
      try {
        await geoguesserService.deleteLevel(id);
        succeeded.push(id);
      } catch (err) {
        failures.push({
          id,
          message: errorDetail(err, 'Delete failed'),
        });
      }
    }
    if (succeeded.length > 0) {
      updateDraftOverlay((prev) => {
        const next = { ...prev };
        for (const id of succeeded) {
          if (id in next) delete next[id];
        }
        return next;
      });
    }
    setSelectedLevelIds([]);
    await loadLevels();
    if (failures.length === 0) {
      reportSuccess(
        `Deleted ${succeeded.length} level${succeeded.length === 1 ? '' : 's'}.`,
      );
    } else {
      const summary = failures
        .map((f) => `#${f.id}: ${f.message}`)
        .join('; ');
      reportError(
        succeeded.length > 0
          ? `Deleted ${succeeded.length}, ${failures.length} failed — ${summary}`
          : `Delete failed: ${summary}`,
      );
    }
  };

  const toggleLevelSelected = (id) => {
    setSelectedLevelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  if (loading) return <p className="wizard-help">Loading levels…</p>;

  const legacyMapImage =
    mapInfo?.map_image_urls || mapInfo?.map_image || null;
  const singleTargetFloor =
    singleFloorId != null && singleFloorId !== ''
      ? floorById[singleFloorId]
      : null;
  const singleFloorImage = resolveFloorCanvasImage(singleTargetFloor);
  const hasFloors = Array.isArray(floors) && floors.length > 0;

  return (
    <section>
      <div className="wizard-section-head">
        <div>
          <h3>Levels</h3>
          <p className="wizard-map-inventory-line" aria-live="polite">
            <strong>{levels.length}</strong> saved level{levels.length === 1 ? '' : 's'} on this map
            {' · '}
            <strong>{floors?.length ?? 0}</strong> floor{(floors?.length ?? 0) === 1 ? '' : 's'}
            {mapInfo?.game_name && (
              <>
                {' · '}
                Game: <strong>{mapInfo.game_name}</strong>
              </>
            )}
          </p>
          <p className="wizard-help">
            <strong>Existing levels:</strong> reassign a floor and re-pin
            answers below, then save changes.{' '}
            <strong>Add one</strong> or <strong>add many</strong> to grow
            the map. Draft picks auto-save when enabled.
          </p>
        </div>
        {enableDraft && (
          <div className="wizard-section-head-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleResetDraft}
              disabled={
                !hydrated || Object.keys(draftOverlay).length === 0
              }
              title="Discard in-progress target-floor and re-pin changes"
            >
              Reset to saved state
            </button>
          </div>
        )}
      </div>

      <div className="wizard-levels-crud-toolbar">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setShowBulkWizard(true);
            setSingleAddOpen(false);
            resetSingleAddForm();
          }}
          disabled={!hasFloors}
          title={
            hasFloors
              ? 'Import many level photos in one flow'
              : 'Add floors first'
          }
        >
          Add multiple levels
        </button>
        <button
          type="button"
          className={`btn-secondary wizard-levels-toolbar-secondary ${singleAddOpen ? 'is-active' : ''}`}
          onClick={toggleSingleAdd}
        >
          {singleAddOpen ? 'Hide add-one form' : 'Add one level'}
        </button>
      </div>

      {showBulkWizard && (
        <BulkLevelImportWizard
          mapId={mapId}
          floors={floors || []}
          floorBuffers={floorBuffers}
          pickFloorImage={resolveFloorCanvasImage}
          onClose={() => setShowBulkWizard(false)}
          onComplete={() => loadLevels()}
          onError={reportError}
          onSuccess={reportSuccess}
        />
      )}

      {singleAddOpen && (
        <form
          className="wizard-level-single-add"
          onSubmit={handleCreateSingleLevel}
        >
          {!hasFloors ? (
            <p className="wizard-help wizard-level-single-add-hint" role="alert">
              Add at least one floor in the section above before creating
              levels.
            </p>
          ) : (
            <>
              <div className="wizard-level-single-add-grid">
                <label className="wizard-level-single-field">
                  <span>Name</span>
                  <input
                    type="text"
                    value={singleName}
                    onChange={(e) => setSingleName(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </label>
                <label className="wizard-level-single-field">
                  <span>Floor</span>
                  <select
                    value={singleFloorId === '' ? '' : String(singleFloorId)}
                    onChange={(e) =>
                      setSingleFloorId(
                        e.target.value ? Number(e.target.value) : '',
                      )
                    }
                    required
                  >
                    <option value="">Select floor…</option>
                    {(floors || []).map((f) => {
                      const liveName =
                        (floorBuffers[f.id]?.name || '').trim() || f.name;
                      return (
                        <option key={f.id} value={f.id}>
                          {liveName}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="wizard-level-single-field">
                  <span>Difficulty</span>
                  <select
                    value={singleDifficulty}
                    onChange={(e) => setSingleDifficulty(e.target.value)}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </label>
                <label className="wizard-level-single-field wizard-level-single-field-wide">
                  <span>Hint (optional)</span>
                  <input
                    type="text"
                    value={singleHint}
                    onChange={(e) => setSingleHint(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="wizard-level-single-field wizard-level-single-field-wide">
                  <span>Level photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSinglePhotoChange}
                  />
                </label>
              </div>
              {singlePhotoPreview && (
                <div className="wizard-level-single-photo-preview">
                  <img src={singlePhotoPreview} alt="" />
                </div>
              )}
              <div className="wizard-level-single-pin">
                {singleTargetFloor && singleFloorImage ? (
                  <>
                    <p className="wizard-level-single-pin-label">
                      Answer location on{' '}
                      {(floorBuffers[singleTargetFloor.id]?.name || '').trim() ||
                        singleTargetFloor.name}
                    </p>
                    <div
                      className="wizard-level-single-pin-stage"
                      style={{ aspectRatio: '16 / 9' }}
                    >
                      <MapPinSelector
                        mapImage={singleFloorImage}
                        pinPosition={singleAnswer}
                        onPinChange={(x, y) => setSingleAnswer({ x, y })}
                        enableZoomPan
                        showZoomControls
                        mode="admin"
                      />
                    </div>
                  </>
                ) : (
                  <p className="wizard-help">
                    Pick a floor that has a map image to place the answer.
                  </p>
                )}
              </div>
              <div className="wizard-level-single-add-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={singleSubmitting}
                >
                  {singleSubmitting ? 'Creating…' : 'Create level'}
                </button>
              </div>
            </>
          )}
        </form>
      )}

      {levels.length === 0 && (
        <p className="wizard-empty">No levels under this map.</p>
      )}

      <div className="wizard-level-list">
        {levels.map((lvl) => {
          const buf = levelBuffers[lvl.id] || {};
          const currentFloorId =
            lvl.floor_id ||
            lvl.floor?.id ||
            (typeof lvl.floor === 'number' ? lvl.floor : null);
          const targetFloor = floorById[buf.floor_id] || null;
          const currentFloor = floorById[currentFloorId] || null;
          const pending = buf.pending_answer;
          const targetFloorBuffer = targetFloor
            ? floorBuffers[targetFloor.id]
            : null;
          const imageChanged = !!targetFloorBuffer?.file;
          const targetMainImage = resolveFloorCanvasImage(targetFloor);
          const targetLiveName = targetFloor
            ? (floorBuffers[targetFloor.id]?.name || '').trim() ||
              targetFloor.name
            : null;
          // What the admin is *doing* in this session, relative to
          // the level's saved server state. We only surface the
          // "previous answer" reference overlay when at least one of
          // these is true — otherwise the main canvas below is
          // already showing the exact saved state, so the overlay is
          // redundant (and was previously confusing users).
          const hasTargetChange =
            buf.floor_id != null && buf.floor_id !== currentFloorId;
          const hasPendingPin = !!pending;
          const isDirty = hasTargetChange || imageChanged || hasPendingPin;

          // Main-canvas pin:
          //   - pending pin from this session, OR
          //   - the saved answer, but only when nothing has changed
          //     (target still points at the current floor and the
          //     image hasn't been swapped out for a new upload).
          const answerForTargetFloor = pending
            ? { x: pending.x, y: pending.y }
            : !isDirty && buf.answer_x != null && buf.answer_y != null
            ? { x: buf.answer_x, y: buf.answer_y }
            : null;
          const needsRepin = (hasTargetChange || imageChanged) && !pending;
          const previewTitle = !targetFloor
            ? 'Preview (pick a target floor)'
            : pending
            ? `New answer on ${targetLiveName}`
            : imageChanged
            ? `New image for ${targetLiveName} — re-pin required`
            : hasTargetChange
            ? `Needs re-pin on ${targetLiveName}`
            : `Answer on ${targetLiveName} (click to move)`;
          const levelLabel = lvl.name || `Level ${lvl.id}`;
          // The level's saved answer, rendered in the top-right
          // overlay. Only shown when the admin is actively changing
          // something; at rest the main canvas already displays this
          // state directly so repeating it would just be noise.
          //
          // Image preference: if the admin uploaded a new image to
          // the *current* floor (same as target), `floor_image` has
          // changed underneath the saved coords and we should show
          // the legacy map_image (the image the coords were actually
          // pinned against) instead. Otherwise the floor's current
          // image is the truth.
          const savedAnswerImage = (() => {
            const floorImg = pickFloorImage(currentFloor);
            if (!floorImg) return legacyMapImage;
            if (imageChanged && targetFloor?.id === currentFloor?.id) {
              return legacyMapImage || floorImg;
            }
            return floorImg;
          })();
          const hasSavedAnswer =
            lvl.answer_x != null &&
            lvl.answer_y != null &&
            currentFloor &&
            savedAnswerImage;
          const previewRef =
            isDirty && hasSavedAnswer
              ? {
                  imageUrl: savedAnswerImage,
                  label: imageChanged
                    ? `Previous ${currentFloor.name} image`
                    : `Saved answer on ${currentFloor.name}`,
                  answer: { x: lvl.answer_x, y: lvl.answer_y },
                }
              : null;

          return (
            <article
              key={lvl.id}
              className={`wizard-level-card ${needsRepin ? 'needs-repin' : ''}`}
            >
              <button
                type="button"
                className="wizard-level-delete-icon"
                onClick={() => handleDeleteLevel(lvl)}
                aria-label={`Delete level ${levelLabel}`}
                title="Delete level"
              >
                <span className="material-symbols-outlined" aria-hidden>
                  delete
                </span>
              </button>
              <header className="wizard-level-card-head has-bulk-select">
                <label className="wizard-level-select-cell">
                  <input
                    type="checkbox"
                    checked={selectedLevelIds.includes(lvl.id)}
                    onChange={() => toggleLevelSelected(lvl.id)}
                    aria-label={`Select ${levelLabel} for removal`}
                  />
                </label>
                <button
                  type="button"
                  className="wizard-level-thumb"
                  onClick={() => setZoomLevelId(lvl.id)}
                  disabled={!lvl.photo}
                  title={lvl.photo ? 'Zoom photo' : 'No photo'}
                  aria-label={`Zoom photo for ${levelLabel}`}
                >
                  {lvl.photo ? (
                    <ProgressiveImage
                      urls={lvl.photo_urls}
                      src={lvl.photo}
                      alt={levelLabel}
                      objectFit="cover"
                    />
                  ) : (
                    <span className="wizard-level-thumb-empty">No photo</span>
                  )}
                </button>

                <div className="wizard-level-info">
                  <div className="wizard-level-name">
                    <span className="wizard-level-name-text">{levelLabel}</span>
                    <span className="wizard-level-difficulty">{lvl.difficulty}</span>
                    {lvl.hint && (
                      <span className="wizard-level-hint" title={lvl.hint}>
                        hint
                      </span>
                    )}
                  </div>
                  <dl className="wizard-level-meta">
                    <div>
                      <dt>Current floor</dt>
                      <dd>{currentFloor?.name || '—'}</dd>
                    </div>
                    <div>
                      <dt>Saved answer</dt>
                      <dd>
                        {buf.answer_x != null && buf.answer_y != null
                          ? `(${buf.answer_x.toFixed(3)}, ${buf.answer_y.toFixed(3)})`
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                </div>

                <label className="wizard-level-target">
                  <span className="wizard-level-target-label">Target floor</span>
                  <select
                    value={buf.floor_id ?? ''}
                    onChange={(e) => {
                      const value = e.target.value
                        ? Number(e.target.value)
                        : null;
                      handleLevelBufferChange(lvl.id, {
                        floor_id: value,
                        pending_answer: null,
                      });
                    }}
                  >
                    <option value="">Select floor…</option>
                    {(floors || []).map((f) => {
                      const liveName =
                        (floorBuffers[f.id]?.name || '').trim() || f.name;
                      return (
                        <option key={f.id} value={f.id}>
                          {liveName}
                          {f.id === currentFloorId ? ' (current)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </header>

              <div className="wizard-level-preview-bar">
                <div className="wizard-level-preview-title">
                  <span className="wizard-level-preview-dot" aria-hidden />
                  {previewTitle}
                  {levelHasPending(lvl) && (
                    <span
                      className="wizard-level-pending-chip"
                      title="Unsaved changes on this row"
                    >
                      Unsaved
                    </span>
                  )}
                </div>
                <div className="wizard-level-preview-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setPinningLevelId(lvl.id)}
                    disabled={!targetFloor}
                    title="Open full-screen pin picker with zoom + pan"
                  >
                    {pending ? 'Adjust pin (zoom)' : 'Open zoom picker'}
                  </button>
                </div>
              </div>

              <div className="wizard-level-preview-map">
                {targetFloor && targetMainImage ? (
                  <div className="wizard-level-preview-stage">
                    <div
                      className="wizard-level-preview-image"
                      style={{ aspectRatio: '16 / 9' }}
                    >
                      <MapPinSelector
                        mapImage={targetMainImage}
                        pinPosition={answerForTargetFloor}
                        onPinChange={(x, y) => handlePinSelect(lvl.id, x, y)}
                        enableZoomPan={false}
                        showZoomControls={false}
                        showAnswer={null}
                        showAnswerMarker={false}
                        mode="admin"
                      />
                    </div>

                    {previewRef && (
                      <aside
                        className="wizard-preview-previous"
                        aria-label="Previous answer reference"
                      >
                        <div className="wizard-preview-previous-head">
                          <span className="material-symbols-outlined">
                            history
                          </span>
                          <span className="wizard-preview-previous-title">
                            {previewRef.label}
                          </span>
                        </div>
                        <div className="wizard-preview-previous-map">
                          <MapCoordinateCanvas
                            imageUrl={previewRef.imageUrl}
                            answer={previewRef.answer}
                            showLine={false}
                            enableZoomPan={false}
                            showZoomControls={false}
                            aspectRatio="16 / 9"
                            style={{ aspectRatio: '16 / 9' }}
                          />
                        </div>
                        <div className="wizard-preview-previous-coords">
                          ({previewRef.answer.x.toFixed(3)},{' '}
                          {previewRef.answer.y.toFixed(3)})
                        </div>
                      </aside>
                    )}
                  </div>
                ) : (
                  <div className="wizard-level-preview-placeholder">
                    {targetFloor
                      ? 'Drop an image on this floor, or click "Open zoom picker" to place the answer.'
                      : 'Pick a target floor above to preview and pin the answer.'}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {levels.length > 0 && selectedLevelIds.length > 0 && (
        <div
          className="wizard-level-bulk-delete-bar"
          role="region"
          aria-label="Bulk delete levels"
        >
          <span className="wizard-level-bulk-delete-count">
            {selectedLevelIds.length} selected
          </span>
          <button
            type="button"
            className="btn-danger"
            onClick={handleBulkDeleteSelected}
          >
            Delete selected
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSelectedLevelIds([])}
          >
            Clear selection
          </button>
        </div>
      )}

      {levels.length > 0 && (
        <div className="wizard-level-save-bar">
          <div className="wizard-level-save-summary">
            {pendingLevels.length === 0 ? (
              <span className="wizard-level-save-state is-clean">
                All level changes saved.
              </span>
            ) : (
              <span className="wizard-level-save-state is-dirty">
                {pendingLevels.length} level
                {pendingLevels.length === 1 ? '' : 's'} with unsaved changes
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn-primary wizard-level-save-btn"
            onClick={handleSaveAllPending}
            disabled={pendingLevels.length === 0 || savingAll}
          >
            {savingAll
              ? 'Saving…'
              : pendingLevels.length === 0
              ? 'Save changes'
              : `Save changes (${pendingLevels.length})`}
          </button>
        </div>
      )}

      {/* Full-screen zoom/pan pin picker */}
      {pinningLevelId != null &&
        (() => {
          const level = levels.find((l) => l.id === pinningLevelId);
          if (!level) return null;
          const buf = levelBuffers[pinningLevelId] || {};
          const targetFloor = floorById[buf.floor_id] || null;
          if (!targetFloor) return null;
          const currentFloorId =
            level.floor_id ||
            level.floor?.id ||
            (typeof level.floor === 'number' ? level.floor : null);
          const currentFloor = floorById[currentFloorId] || null;
          const targetFloorBuffer = floorBuffers[targetFloor.id];
          const imageChanged = !!targetFloorBuffer?.file;
          const targetMainImage = resolveFloorCanvasImage(targetFloor);
          const hasTargetChange =
            buf.floor_id != null && buf.floor_id !== currentFloorId;
          // When target == current AND no new image has been uploaded
          // to that floor, the saved answer coordinates still apply
          // to the picker canvas, so we render them as a read-only
          // reference marker. When target != current or there's a
          // fresh image, the coords don't match the canvas anymore
          // and we move them into the small "previous answer"
          // overlay in the top-right instead.
          const savedAppliesToCanvas =
            !hasTargetChange &&
            !imageChanged &&
            level.answer_x != null &&
            level.answer_y != null;
          const existingOnSameFloor = savedAppliesToCanvas
            ? { x: level.answer_x, y: level.answer_y }
            : null;
          const pinPosition =
            buf.pending_answer ||
            (savedAppliesToCanvas
              ? { x: buf.answer_x, y: buf.answer_y }
              : null);
          const showPreviousOverlay =
            (hasTargetChange || imageChanged) &&
            currentFloor &&
            level.answer_x != null &&
            level.answer_y != null;
          const previousImageUrl = (() => {
            // Image that the saved coords were pinned against.
            // If the current floor is the target AND we're staring
            // at a freshly-uploaded image, the floor's current
            // floor_image is the *new* one, so fall back to the
            // legacy map_image when available.
            const floorImg = pickFloorImage(currentFloor);
            const legacy =
              mapInfo?.map_image_urls || mapInfo?.map_image || null;
            if (imageChanged && targetFloor?.id === currentFloor?.id) {
              return legacy || floorImg;
            }
            return floorImg || legacy;
          })();
          const previousLabel = imageChanged
            ? 'Previous image'
            : 'Previous answer';
          const previousSubLabel = imageChanged
            ? `${currentFloor?.name || ''} (before replace)`
            : `on ${currentFloor?.name || ''}`;
          return (
            <div className="map-selector-overlay">
              <div className="map-selector-container wizard-repin-container">
                <div className="map-selector-header">
                  <div>
                    <h4>
                      Re-pin &quot;{level.name || `Level ${level.id}`}&quot; on{' '}
                      {targetFloor.name}
                      {imageChanged ? ' (new image)' : ''}
                    </h4>
                    <p className="map-selector-legend">
                      {existingOnSameFloor && (
                        <span className="legend-item">
                          <span className="legend-marker answer-marker">✓</span>{' '}
                          Previous answer
                        </span>
                      )}
                      <span className="legend-item">
                        <span className="legend-marker pin-marker">📍</span>{' '}
                        New pin
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => setPinningLevelId(null)}
                    className="close-btn"
                    type="button"
                  >
                    ×
                  </button>
                </div>
                <div className="wizard-repin-stage">
                  <MapPinSelector
                    mapImage={targetMainImage}
                    pinPosition={pinPosition}
                    onPinChange={(x, y) =>
                      handlePinSelect(pinningLevelId, x, y)
                    }
                    enableZoomPan
                    showZoomControls
                    showAnswer={existingOnSameFloor}
                    showAnswerMarker={!!existingOnSameFloor}
                    mode="admin"
                    onSubmit={() => setPinningLevelId(null)}
                    onCancel={() => setPinningLevelId(null)}
                  />
                  {showPreviousOverlay && previousImageUrl && (
                    <aside
                      className="wizard-repin-previous"
                      aria-label="Previous answer reference"
                    >
                      <div className="wizard-repin-previous-head">
                        <span className="material-symbols-outlined">
                          history
                        </span>
                        <div>
                          <div className="wizard-repin-previous-title">
                            {previousLabel}
                          </div>
                          <div className="wizard-repin-previous-sub">
                            {previousSubLabel}
                          </div>
                        </div>
                      </div>
                      <div className="wizard-repin-previous-map">
                        <MapCoordinateCanvas
                          imageUrl={previousImageUrl}
                          answer={{
                            x: level.answer_x,
                            y: level.answer_y,
                          }}
                          showLine={false}
                          enableZoomPan={false}
                          showZoomControls={false}
                          aspectRatio="16 / 9"
                          style={{ aspectRatio: '16 / 9' }}
                        />
                      </div>
                      <div className="wizard-repin-previous-coords">
                        ({level.answer_x.toFixed(3)},{' '}
                        {level.answer_y.toFixed(3)})
                      </div>
                    </aside>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {zoomLevelId != null &&
        (() => {
          const level = levels.find((l) => l.id === zoomLevelId);
          if (!level?.photo) return null;
          return (
            <PhotoZoomModal
              imageUrl={level.photo_urls || level.photo}
              alt={level.name || `Level ${level.id}`}
              title={level.name || `Level ${level.id}`}
              onClose={() => setZoomLevelId(null)}
            />
          );
        })()}
    </section>
  );
};

export default LevelAssigner;
